import type { SelectPropTypes } from './types';

import React from 'react';
import {
  type ChakraStylesConfig,
  type GroupBase,
  type OptionBase,
  Select as CkSelect,
} from 'chakra-react-select';

import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Spinner,
} from '@/components';
import { useTranslation } from '@/hooks';

type SelectOptionType = { label: string; value: string } & OptionBase;
const Select: React.FC<SelectPropTypes> = ({
  options,
  error,
  placeholder,
  label,
  onChange,
  isLoading,
  style,
  value,
  isRequired = false,
  isCurve = false,
  isDisabled = false,
  isSearchable = false,
  isMulti = undefined,
  type = 'select',
  variant = 'filled',
  ...rest
}) => {
  const { t } = useTranslation('components.select');
  const chakraStyles: ChakraStylesConfig<
    SelectOptionType,
    true,
    GroupBase<SelectOptionType>
  > = {
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: 'primary.500',
      cursor: 'pointer',
      display: isLoading ? 'none' : 'flex',
      alignItems: 'center',
      '> svg': {
        transform: `rotate(${state.selectProps.menuIsOpen ? -180 : 0}deg)`,
        transition: 'all 0.2s',
      },
    }),
    placeholder: (provided, state) => ({
      ...provided,
      color: state.isFocused ? 'primary.900' : 'primary.700',
      fontSize: 'sm',
    }),
    valueContainer: (provided) => {
      const sortCss =
        type === 'sort'
          ? {
              pl: '0',
              pr: '8px',
              flex: 'unset',
              display: 'flex',
              flexDir: 'row-reverse',
            }
          : {};
      return {
        ...provided,
        fontSize: 'sm',
        maxWidth: isMulti && '160px',
        color: 'primary.700',
        cursor: 'pointer',
        fontWeight: 400,
        ...sortCss,
      };
    },
    noOptionsMessage: (provided) => ({ ...provided, fontSize: 'sm' }),
    option: (provided) => ({
      ...provided,
      color: 'primary.900',
      ps: 1,
      paddingBlock: 2,
      fontSize: 'sm',
      fontWeight: 400,
      bg: 'white',
      _hover: { bg: 'secondary.50' },
      _selected: { bg: 'secondary.50' },
    }),
    menuList: (provided) => ({
      ...provided,
      zIndex: 'dropdown',
      px: 4,
      py: 2,
      bg: 'white',
      _hover: { bg: 'white !important' },
    }),
    menu: (provided) => ({ ...provided, zIndex: 'dropdown' }),
    control: (provided, state) => ({
      ...provided,
      minW: '163px',
      justifyContent: type === 'sort' ? 'center' : 'space-between',
      // eslint-disable-next-line no-nested-ternary
      borderRadius: isCurve ? 'xl' : state.isFocused ? 'xl' : 'curved',
      bgColor: variant === 'outline' ? 'white' : 'gray.250',
    }),
  };
  return (
    <FormControl
      isInvalid={Boolean(error)}
      isRequired={isRequired}
      w="max-content"
      style={{ ...style }}
    >
      {label && <FormLabel>{label}</FormLabel>}
      <CkSelect
        isLoading={isLoading}
        components={{
          LoadingIndicator: () => (
            <Spinner size="sm" color="primary.500" marginLeft={3} />
          ),
        }}
        value={value}
        isSearchable={isSearchable}
        noOptionsMessage={() => <>{t('noOption')}</>}
        chakraStyles={chakraStyles}
        placeholder={placeholder || t('choose')}
        options={options}
        onChange={onChange}
        isMulti={isMulti}
        variant={variant}
        isDisabled={isDisabled}
        {...rest}
      />
      <FormErrorMessage message={error || ''} />
    </FormControl>
  );
};
Select.displayName = 'Select';

export default Select;
